// The EBS panel — water-column structure for an Environmental Baseline Survey.
//
// An EBS is one of TDI-Brooks' founding service lines (both founders came out of
// Texas A&M's Geochemical and ENVIRONMENTAL Research Group). On a pre-drill EBS
// they run a CTD/rosette for dissolved oxygen, nutrients, TSS and chlorophyll,
// plankton tows, and box coring for benthic infauna and sediment contaminants.
//
// THIS PANEL DOES NOT MEASURE ANY OF THAT, AND MUST NOT LOOK AS THOUGH IT DOES.
// An EBS exists to produce measured, defensible numbers for a regulator. A model
// cannot stand in for a water sample and this console will never pretend
// otherwise. What it can do is describe the PHYSICAL structure of the column
// before the vessel sails — where the mixed layer ends, where the density
// gradient sits, how deep the water is — because that structure is what decides
// where the bottles should be fired, and getting that wrong is paid for in ship
// time rather than in analysis.
//
// So this is a cast-planning aid, and the absences are the load-bearing part.
// The four chemistries an EBS actually reports are named as missing, explicitly,
// on every render. A panel that quietly showed only what it happened to have
// would read as an EBS product, and it is not one.
//
// WHY THE MIXED-LAYER ROW IS THE POINT. This is not a guess about what an EBS
// cast wants. The public Yellowtail EIA records the depth rules TDI-Brooks and
// its partners actually used off Guyana, and the 2019 Stabroek campaign fired
// its mid-water bottle at "the base of the mixed layer" — 34 to 69 m depending
// on the station. That is precisely the number this panel computes. A round
// number would have missed it by tens of metres.
//
// (The biogeochemistry IS partly available — Copernicus models oxygen,
// chlorophyll and nutrients at 1/4°. It is not fetched, and the reason is now
// narrower than "scope": only surface chlorophyll is assimilated, oxygen and
// nutrients are damped toward a climatology on a one-year relaxation, and the
// model's 12 m minimum depth means it cannot see a shelf dead zone at all. Those
// are limits of the data, and they would have to be gated, not just fetched.)

import type { PointForecast } from "../forecast/openmeteo.js";
import type { SvpResult } from "../data/api.js";
import { fmtDepth } from "../ui/units.js";
import type { Fact } from "./factpanel.js";

/** Temperature and salinity at the shallowest stored level. */
function surface(svp: SvpResult): { tempC: number; sal: number } | null {
  const r = svp.cell.p.find(
    (row) => row.length >= 3 && Number.isFinite(row[1]) && Number.isFinite(row[2]),
  );
  return r === undefined ? null : { tempC: r[1], sal: r[2] };
}

export interface EbsInput {
  fc: PointForecast;
  svp: SvpResult | null;
  svpCovered: boolean;
}

export function ebsFacts(input: EbsInput): Fact[] {
  const { fc, svp, svpCovered } = input;
  const facts: Fact[] = [];

  const depth = fc.depthM;
  facts.push({
    label: "Water depth",
    value: depth === null ? null : fmtDepth(depth),
    absent: "no charted depth at this point",
    why: "Sets the deepest bottle and the bottom-water sample; also whether a box core is recoverable in a working day.",
  });

  const relief = fc.relief;
  facts.push({
    label: "Seabed relief (~5 km)",
    value:
      relief === null
        ? null
        : `${fmtDepth(relief.shallow)} – ${fmtDepth(relief.deep)} (${fmtDepth(relief.deep - relief.shallow)} range)`,
    absent: "no bathymetric statistics here",
    why: "Benthic stations want ground that represents the block. Strong relief inside one station's radius means two habitats, not one.",
    caution: relief !== null && relief.deep - relief.shallow > 200,
  });

  if (!svpCovered || svp === null) {
    facts.push({
      label: "Water-column structure",
      value: null,
      absent: svpCovered ? "profile did not load" : "no model cell at this point",
      why: "Without it there is nothing here to plan bottle depths against.",
      caution: true,
    });
  } else {
    const c = svp.cell;
    const s = surface(svp);
    facts.push({
      label: "Mixed layer",
      value: c.mld === null ? "none resolved — column is stratified from the surface" : fmtDepth(c.mld),
      why: "Above this the water is one body, so samples within it duplicate each other. The first real change is at its base — which is where a cast should fire, and it moves: on the 2019 Stabroek EBS the mid-water bottle was taken at 'the base of the mixed layer' and that came out anywhere from 34 to 69 m across stations.",
    });
    facts.push({
      label: "Strongest gradient",
      value:
        c.th === null
          ? "well mixed — no marked thermocline"
          : `${fmtDepth(c.th[0])} – ${fmtDepth(c.th[1])}, ${c.th[2].toFixed(1)} °C drop`,
      why: "The layer a rosette should straddle rather than average across. In a stratified column this is also where the oxygen minimum and the chlorophyll maximum tend to sit — tend to, which is why it is sampled and not assumed.",
      caution: c.th !== null && c.th[2] >= 4,
    });
    facts.push({
      label: "Surface T / S",
      value: s === null ? null : `${s.tempC.toFixed(1)} °C · ${s.sal.toFixed(2)} PSU`,
      absent: "no surface level in the profile",
      why: "The near-surface end-member, and the expectation a CTD's first metres should land near.",
    });
  }

  // The chemistries an EBS reports. Named as absent on every render, because a
  // panel titled EBS that silently omitted them would be read as saying there
  // was nothing to report.
  facts.push({
    label: "Dissolved oxygen",
    value: null,
    absent: "not fetched",
    why: "Modelled by Copernicus at 1/4°, but oxygen and nutrients are unconstrained by observation — damped toward a climatology on a one-year relaxation — and on a shallow shelf the model's 12 m floor cannot resolve near-bottom water at all. Not fetched, and it would need gating before it could be.",
  });
  facts.push({
    label: "Nutrients · chlorophyll · TSS",
    value: null,
    absent: "not fetched",
    why: "Nitrate, phosphate, silicate and chlorophyll are modelled at 1/4°. That is ~684 km² per cell at 28°N against a 23 km² BOEM lease block — ONE cell spans about 29 blocks. Regime and season only; nothing at this scale can site a station.",
  });

  return facts;
}

export const EBS_NOTE =
  "Physical structure only, modelled, for planning a cast. It is not a baseline, " +
  "not a measurement, and cannot be submitted as either — an EBS is answered with " +
  "water in bottles and sediment in cores.";
